/* eslint-disable jsx-a11y/label-has-associated-control -- label+htmlFor+id é a
   associação usada nos formulários do app; a regra do airbnb exige TAMBÉM o
   aninhamento, o que quebraria o grid do Field. */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import Loading from '../../components/Loading';
import { notifyApiError } from '../../utils/feedback';
import { createUser } from '../../services/users/usersApi';
import { listRoles } from '../../services/users/rolesApi';

import { Container } from '../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
} from '../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../styles/layout/Section';
import { Field, RequiredMark } from '../../styles/forms/Field';
import { FormGrid } from '../../styles/layout/FormGrid';
import {
   ActionBar,
   ActionBarLeft,
   ActionBarRight,
} from '../../styles/layout/ActionBar';
import { PrimaryButton, GhostButton } from '../../styles/ui/Button';
import { FieldError, FieldHint } from '../../styles/ui/Composer';

// ---------------------------------------------------------------------------
// Usuários — criação (handoff 0110).
//
// Migrada em 2026-09-14: `POST /users` (a rota `/users/new` morreu), com
// `password_confirm` no payload e `role_id` OBRIGATÓRIO — o fallback silencioso
// para o papel "visualizador" acabou no Gen3.
//
// Feedback pelo FC4: o que tem campo vai INLINE (422 de validação e o 409
// `email_in_use`, que o backend devolve com `field: "email"`); o resto vai por
// `notifyApiError`. Nunca os dois ao mesmo tempo.
//
// O e-mail é chave de login e IMUTÁVEL depois de criado — daí o aviso no campo.
// ---------------------------------------------------------------------------

const EMPTY_FORM = {
   nome: '',
   email: '',
   role_id: '',
   password: '',
   password_confirm: '',
};

export default function CreateUser() {
   const navigate = useNavigate();

   const [roles, setRoles] = useState([]);
   const [isLoadingRoles, setIsLoadingRoles] = useState(false);
   const [rolesDenied, setRolesDenied] = useState(false);
   const [isSubmitting, setIsSubmitting] = useState(false);

   const [form, setForm] = useState(EMPTY_FORM);
   const [fieldErrors, setFieldErrors] = useState({});

   useEffect(() => {
      let mounted = true;

      // Dependência CROSS-MODULE: `GET /roles` exige `iam:role:read`, e esta
      // tela é gateada por `iam:user:create`. Quem tem uma chave e não a outra
      // tomaria 403 — e, sem a flag, o interceptor global trocaria a tela por
      // `/403` com reload. Aqui o papel é obrigatório para criar, então o
      // desfecho honesto é dizer o que falta, não sumir com a tela.
      async function loadRoles() {
         try {
            setIsLoadingRoles(true);
            const list = await listRoles({ skipForbiddenRedirect: true });
            if (mounted) setRoles(list);
         } catch (err) {
            if (!mounted) return;
            if (err?.kind === 'forbidden') {
               setRolesDenied(true);
            } else {
               notifyApiError(err, { fallback: 'Erro ao carregar os papéis.' });
            }
         } finally {
            if (mounted) setIsLoadingRoles(false);
         }
      }

      loadRoles();
      return () => {
         mounted = false;
      };
   }, []);

   const onChange = (e) => {
      const { name, value } = e.target;
      setForm((prev) => ({ ...prev, [name]: value }));
      setFieldErrors((prev) => {
         if (!prev[name]) return prev;
         const next = { ...prev };
         delete next[name];
         return next;
      });
   };

   // Espelha as regras do backend só para evitar um round-trip óbvio — a
   // validação de verdade continua sendo a de lá (Zod), e é ela que manda na
   // mensagem quando as duas discordam.
   const validate = () => {
      const errors = {};

      if (!form.nome || form.nome.trim().length < 3) {
         errors.nome = 'Informe ao menos 3 caracteres.';
      }
      if (!form.email || !/^\S+@\S+\.\S+$/.test(form.email)) {
         errors.email = 'Informe um e-mail válido.';
      }
      if (!form.role_id) {
         errors.role_id = 'Selecione o papel de acesso.';
      }
      if (
         !form.password ||
         form.password.length < 6 ||
         form.password.length > 50
      ) {
         errors.password = 'A senha precisa ter entre 6 e 50 caracteres.';
      }
      if (form.password !== form.password_confirm) {
         errors.password_confirm = 'A confirmação não confere com a senha.';
      }

      return errors;
   };

   const handleSubmit = async (e) => {
      e.preventDefault();

      const localErrors = validate();
      if (Object.keys(localErrors).length) {
         setFieldErrors(localErrors);
         return;
      }

      try {
         setIsSubmitting(true);
         setFieldErrors({});

         const { user, message } = await createUser({
            nome: form.nome.trim(),
            email: form.email.trim().toLowerCase(),
            roleId: form.role_id,
            password: form.password,
            passwordConfirm: form.password_confirm,
         });

         toast.success(message);
         // Vai para a ficha: é lá que se concede PDV, e concessão é o passo
         // seguinte natural de "acabei de criar a conta".
         navigate(`/users/${user?.id}`);
      } catch (err) {
         const apiFieldErrors = err?.fieldErrors ?? {};
         if (Object.keys(apiFieldErrors).length) {
            setFieldErrors(apiFieldErrors);
         } else {
            notifyApiError(err, { fallback: 'Erro ao criar usuário.' });
         }
      } finally {
         setIsSubmitting(false);
      }
   };

   const isBusy = isSubmitting || isLoadingRoles;

   return (
      <Container>
         <Loading isLoading={isSubmitting} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Criar usuário</PageTitle>
               <PageSubtitle>
                  Cadastre uma conta de acesso e defina o papel de permissões.
               </PageSubtitle>
            </PageHeaderText>
         </PageHeader>

         <form onSubmit={handleSubmit}>
            <Section>
               <SectionHeader>
                  <SectionTitle>Dados do usuário</SectionTitle>
               </SectionHeader>
               <SectionBody>
                  <FormGrid>
                     <Field className="col-6">
                        <label htmlFor="nome">
                           Nome
                           <RequiredMark>*</RequiredMark>
                        </label>
                        <input
                           id="nome"
                           name="nome"
                           value={form.nome}
                           onChange={onChange}
                           placeholder="Ex.: João Silva"
                           autoComplete="name"
                           disabled={isBusy}
                           aria-invalid={!!fieldErrors.nome || undefined}
                        />
                        {fieldErrors.nome && (
                           <FieldError>{fieldErrors.nome}</FieldError>
                        )}
                     </Field>

                     <Field className="col-6">
                        <label htmlFor="email">
                           E-mail
                           <RequiredMark>*</RequiredMark>
                        </label>
                        <input
                           id="email"
                           name="email"
                           type="email"
                           value={form.email}
                           onChange={onChange}
                           placeholder="Ex.: joao@empresa.com"
                           autoComplete="email"
                           disabled={isBusy}
                           aria-invalid={!!fieldErrors.email || undefined}
                        />
                        {fieldErrors.email ? (
                           <FieldError>{fieldErrors.email}</FieldError>
                        ) : (
                           <FieldHint>
                              É a chave de login e não poderá ser alterada
                              depois.
                           </FieldHint>
                        )}
                     </Field>

                     <Field className="col-6">
                        <label htmlFor="role_id">
                           Papel
                           <RequiredMark>*</RequiredMark>
                        </label>
                        <select
                           id="role_id"
                           name="role_id"
                           value={form.role_id}
                           onChange={onChange}
                           disabled={isBusy}
                           aria-invalid={!!fieldErrors.role_id || undefined}
                        >
                           <option value="">
                              {isLoadingRoles
                                 ? 'Carregando...'
                                 : 'Selecione o papel'}
                           </option>
                           {roles.map((role) => (
                              <option key={role.id} value={String(role.id)}>
                                 {role.name}
                              </option>
                           ))}
                        </select>
                        {fieldErrors.role_id && (
                           <FieldError>{fieldErrors.role_id}</FieldError>
                        )}
                        {rolesDenied && !fieldErrors.role_id && (
                           <FieldError>
                              Não foi possível listar os papéis: falta a
                              permissão <code>iam:role:read</code>. Sem ela não
                              dá para criar o usuário — peça a um
                              administrador.
                           </FieldError>
                        )}
                     </Field>
                  </FormGrid>
               </SectionBody>
            </Section>

            <Section style={{ marginTop: 14 }}>
               <SectionHeader>
                  <SectionTitle>Senha de acesso</SectionTitle>
               </SectionHeader>
               <SectionBody>
                  <FormGrid>
                     <Field className="col-6">
                        <label htmlFor="password">
                           Senha
                           <RequiredMark>*</RequiredMark>
                        </label>
                        <input
                           id="password"
                           name="password"
                           type="password"
                           value={form.password}
                           onChange={onChange}
                           placeholder="6 a 50 caracteres"
                           autoComplete="new-password"
                           disabled={isBusy}
                           aria-invalid={!!fieldErrors.password || undefined}
                        />
                        {fieldErrors.password && (
                           <FieldError>{fieldErrors.password}</FieldError>
                        )}
                     </Field>

                     <Field className="col-6">
                        <label htmlFor="password_confirm">
                           Confirmar senha
                           <RequiredMark>*</RequiredMark>
                        </label>
                        <input
                           id="password_confirm"
                           name="password_confirm"
                           type="password"
                           value={form.password_confirm}
                           onChange={onChange}
                           autoComplete="new-password"
                           disabled={isBusy}
                           aria-invalid={
                              !!fieldErrors.password_confirm || undefined
                           }
                        />
                        {fieldErrors.password_confirm && (
                           <FieldError>
                              {fieldErrors.password_confirm}
                           </FieldError>
                        )}
                     </Field>
                  </FormGrid>

                  <ActionBar style={{ marginTop: 16 }}>
                     <ActionBarLeft />
                     <ActionBarRight>
                        <GhostButton
                           type="button"
                           onClick={() => navigate('/users')}
                           disabled={isSubmitting}
                        >
                           Cancelar
                        </GhostButton>
                        <PrimaryButton type="submit" disabled={isBusy}>
                           {isSubmitting ? 'Criando...' : 'Criar usuário'}
                        </PrimaryButton>
                     </ActionBarRight>
                  </ActionBar>
               </SectionBody>
            </Section>
         </form>
      </Container>
   );
}
